import React, { useEffect, useState } from "react";
import { 
  Row,
  Col, 
  Alert, 
  Form,
  Input, 
  Button, 
  message,
  Modal,
  ConfigProvider,
  theme } from "antd";
import { useSelector } from 'react-redux';
import TextArea from "antd/es/input/TextArea";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from 'axios';
import { Coinbase_Auth_Token } from "../../const/const";
import { useAuth } from '../../contexts/auth-context';
import './styles/TransactionDetails.css';
import { metamaskSendUSDC, checkMetaMaskAndNetwork } from '../../helpers';
import { getAccountData } from "../../rest/crypto-api/coinbase";
import { useLegalDocDialog } from '../../hooks/legal-doc';

// GK 2023-10-22 - creates the TransactionDetails page, which gets rendered after clicking on
// a transaction in the TransactionList component 
const TransactionDetails = (props) => {
  const storage = localStorage;
  const [transaction, setTransaction] = useState(null);
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const [inProgress, setInProgress] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [completingPayment, setCompletingPayment] = useState(false);
  const [error, setError] = useState(null);
  const [twoFactorRequire, setTwoFactorRequire] = useState(false);
    const [twoFAError, setTwoFAError] = useState(null);
  const [account, setAccount] = useState([]);
  const cryptoAccount = useSelector(state => state.cryptoAccount.account);
  const auth = useAuth();
  const [form] = Form.useForm();
  const loginEmail = auth.currentUser?.email;
  const ethusdc_address = cryptoAccount.data?.ethusdc_address;
  const signedInWithMetamask = cryptoAccount.data?.exchange?.toLowerCase() === 'metamask';
  const signedInWithCoinbase = cryptoAccount.data?.exchange?.toLowerCase() === 'coinbase';

  // GK 2026-04-23 This is required on all pages where the legal docs *need* to be shown
  useLegalDocDialog();

    // GK 2023-10-22 - This fetches an account via Axios
    const fetchCoinbaseAccount = async () => {
      const errorMessage = <div>
        You must be logged in with Coinbase to pay someone. Please <Link to="/crypto-login" class="light-blue">login</Link> to proceed!
      </div>;
  
      try {
        const coinbaseAuthToken = storage.getItem(Coinbase_Auth_Token);
        if(!signedInWithCoinbase) {
          setError('The sender remain be signed into Coinbase or Metamask to complete this transaction');
          return;
        }
        if(!coinbaseAuthToken) {
          setError('The sender remain be signed into Coinbase or Metamask to complete this transaction');
          return;
        }
        const response = await getAccountData(coinbaseAuthToken, 'USDC');
        
        if(response) {
          setAccount(response);
        } else {
          setError(errorMessage);
        }
  
      } catch (error) {
        // If the error response indicates an expired token, sign out the user
        if (error.response && error.response.data.errorCode === 'TOKEN_EXPIRED') {
          auth.signOut();
        }
        setError(errorMessage);
      }
    }
    
  
    // GK 2023-10-22 - This fetches the account when the component renders
    useEffect(() => {
      if (
        transaction?.split_payment_status === 'pending' &&
        transaction?.sender_login_email === loginEmail &&
        transaction?.sender_exchange?.toLowerCase() === 'coinbase'
        // signedInWithCoinbase
      ){
        fetchCoinbaseAccount();
      }
    }, [signedInWithCoinbase]);
    
    // something like "if user is signed in with metamask, then run this:"
    useEffect(() => {
      if (
        transaction?.split_payment_status === 'pending' && 
        transaction?.sender_login_email === loginEmail &&
        transaction?.sender_exchange?.toLowerCase() === 'metamask'
        // signedInWithMetamask
      
      ){
        if(!signedInWithMetamask) {
          setError('The sender be signed into Metamask to complete this transaction');
          return;
        }
        
        checkMetaMaskAndNetwork(ethusdc_address).then(result => {
          if (result.success) {
            console.log("All checks passed. Wallet address:", result.address);
          } else {
            console.error("Check failed:", result.error);
            setError(result.error);
          }
        });
      }
    }, [signedInWithMetamask]);

  // GK 2023-10-22 - gets a specific transaction
  const getTransaction = async (transactionId) => {
    try {
      const response = await axios.post(`/api/transactions/${transactionId}`, {
        accountId: null, //result[0]?.id,
        accessToken: storage.getItem(Coinbase_Auth_Token),
      });
  
      console.log('Mongo Transaction', response.data);
      setTransaction(response.data);
      setInProgress(false);

    } catch (error) {
      console.log('Transaction Error ->', error);
      // If the error response indicates an expired token, sign out the user
      if (error.response && error.response.data.errorCode === 'TOKEN_EXPIRED') {
        auth.signOut();
      }
      setError('Something went wrong. Try again');
      setInProgress(false);
    }
  }

  // GK 2023-10-22 - updates transaction details
  const updateTransaction = async (transactionId, transactionData) => {
    setIsUpdating(true);
    try {
      const response = await axios.patch(`/api/transactions/${transactionId}`, transactionData);
  
      setTransaction(response.data);
      setIsUpdating(false);
      message.success({ content: `Transaction successfully updated`, key: 'transaction-success', duration: 2.5 });

    } catch (error) {
      console.log('Transaction Update Error ->', error);
      // If the error response indicates an expired token, sign out the user
      if (error.response && error.response.data.errorCode === 'TOKEN_EXPIRED') {
        auth.signOut();
      }

      let errMessage = 'Something went wrong! Try again';
      if (error?.response?.data?.message) {
        errMessage = error.response.data.message;
      }
      
      setError(errMessage);
      setIsUpdating(false);
    }
  }

  const getTransactionTypeMessage = (transacion) => {
    // A: This transaction was paid directly to the {email}
    // B: This transaction is paying the recipient's fees which they owe to punkypay. The recipient should consider this a successful payment to them, and should not ask the sender to resend money. Click here to read about Punkypay's fees.
    // C: This transaction was split into two parts. One transaction went toward paying the fees which the recipient owed to punkypay. and the other transaction went directly to the recipient. The recipient should consider this a successful payment to them, and should not ask the sender to resend any payment. Please read about Punkypay's fee structure here. 
    if(transacion.transactionFee) {
      return `This transaction was split into two parts. One transaction went toward paying the fees which the recipient owed to punkypay. and the other transaction went directly to the recipient. The recipient should consider this a successful payment to them, and should not ask the sender to resend any payment. Please read about Punkypay's fee structure here.`;
    } else if (transacion.db_source === 'transaction') {
      return `This transaction was paid directly to ${transacion.recipient_login_email}`;
    } else if (transacion.db_source === 'transactionfee') {
      return `This transaction is paying the recipient's fees which they owe to punkypay. The recipient should consider this a successful payment to them, and should not ask the sender to resend money. Click here to read about Punkypay's fees.`;
    } else {
      return '';
    }
  }

  // GK 2023-10-22 - runs the getTransaction function when you click on the transaction from the TransactionList
  // this is important to update the transaction data (eg. the "Status" from coibnase) when you click on it
  useEffect(() => {
    if(!transactionId) {
      navigate(-1);
      return;
    }

    getTransaction(transactionId);

  }, [transactionId]);

  // GK 2023-10-22 - when you hit submit, you update the transaction
  const onFinish = (value) => {
    console.log("onFinish:", value);
    updateTransaction(transactionId, value);
  };

  const completeSplitPaymentMetamask = async () => {
    try {
      
      const amount = (Number(transaction.total_amount_user_entered)*1000000 - Number(transaction.amount_entered)*1000000)/1000000;
      console.log('completeSplitPaymentMetamask --->', amount);
      console.log('amount ====>', amount);
      const toAndFromData = {
        to: transaction.recipient_login_email, 
        from: loginEmail,
      };

      setCompletingPayment(true);

      const toAndFrom = await axios.post('/api/transactions/get-ethusdc-sender-receiver', toAndFromData);

      const responseSendUSDC = await metamaskSendUSDC(toAndFrom.data.sent_to, toAndFrom.data.sent_from, amount);
      
      const sendCryptoData = {
        to: transaction.recipient_login_email, 
        from: loginEmail, 
        amount,
        currency: 'USDC',
        network_name: 'ethereum', // This should be different based on the variable we put in at the top of this function
        txn_id: responseSendUSDC.txHash,
        gas_cost: responseSendUSDC.gasInfo.estimatedCostUSDC.toString(),
        shortMemo: transaction.memo_at_txn,
        notes: transaction.sender_notes,
        isTransactionFee: false,
        transactionFeeId: transaction._id, // Note this can be undefined. used only to link TransactionFee with its parent Transaction if provided.
        totalAmountUserEntered: transaction.total_amount_user_entered,
        splitPaymentStatus: 'completed', 
      };

      const responseSaveInDatabase = await axios.post('/api/transactions/send-crypto-metamask', sendCryptoData);
      console.log(responseSaveInDatabase);

      if(responseSaveInDatabase) {
        console.log('Transaction Created successfully =>', responseSaveInDatabase.data);
        navigate(`/transactions/${responseSaveInDatabase.data.exchange_txn_id}`);
        setCompletingPayment(false);
      }
    } catch (error) {
      console.log('Transaction Create: Error =>', error);
              // If the error response indicates an expired token, sign out the user
      if (error.response && error.response.data?.errorCode === 'TOKEN_EXPIRED') {
        auth.signOut();
      }
      setCompletingPayment(false);
      
      // if metamask error
      if (error.code === 4001) {
        setError("Transaction rejected by the user.");
        console.error("User denied transaction signature.");
        // Return error information
        return;
      }
      const errorData = error.response?.data;
      if(errorData?.errorCode) { // This means the error is our customized error from the server
        setError(errorData.message);
        return;
      }

      // 2025-02-15 GK
      // I think the below shouldn't exist because we're on Metamask now, and we shouldn't need the below.
      // Double check the error from metmask isn't in an array format...
      console.log('Transaction Create: 2FA Auth required =>', errorData);
      let errorString = "";
      // GK 2023-10-22 - the errorData is an array coming from coinbase, we are looking for the "id" key
      errorData?.errors?.forEach((err) => {
        if (err?.id === "two_factor_required") {
          setTwoFactorRequire(true);
          errorString += 'Please enter two-factor authentication token for Coinbase. You will recieve the token on an authenticator app (like Google Authy), text message, email, or security prompt.'
        } else {
          errorString += err?.message + "\n";
        }
      });

      setError(errorString || error.message);
    }
  }
  

  const completeSplitPaymentCoinbase = async () => {
      try {

        const amount = (Number(transaction.total_amount_user_entered)*1000000 - Number(transaction.amount_entered)*1000000)/1000000;
        console.log('completeSplitPaymentMetamask --->', amount);
        console.log('amount ====>', amount);
        if(Number(amount) < 1) { 
          setError('Amount can\'t be less than 1 for Coinbase transaction.');
          return;
        }
        // const toAndFromData = {
        //   to: transaction.recipient_login_email, 
        //   from: loginEmail,
        // };

        setCompletingPayment(true);

        //const toAndFrom = await axios.post('/api/transactions/get-ethusdc-sender-receiver', toAndFromData);
        
        const formValues = await form.validateFields();

        /**
         * ************************* DEFAULT PAYMENT (1 payment to recipient) ***********************
         * If there is no ghost fee and the amount entered is greater than or equal to coinbase minimum
         * or there is a ghost fee but the ghost fee is not up to the threshold
         */
        console.log("Coinbase txn: Default payment")
        const requestData = {
          accountId: account.id,
          accessToken: storage.getItem(Coinbase_Auth_Token),
          //
          to: transaction.recipient_login_email, 
          from: loginEmail, 
          amount,
          currency: 'USDC',
          network_name: 'ethereum', // This should be different based on the variable we put in at the top of this function
          shortMemo: transaction.memo_at_txn,
          notes: transaction.sender_notes,
          isTransactionFee: false,
          transactionFeeId: transaction._id, // Note this can be undefined. used only to link TransactionFee with its parent Transaction if provided.
          totalAmountUserEntered: transaction.total_amount_user_entered,
          splitPaymentStatus: 'completed', 
          twoFactorToken: twoFactorRequire ? formValues?.twoFactorToken : null,
        };

        const responseSaveInDatabase = await axios.post('/api/transactions/send-crypto-coinbase', requestData);

        if(responseSaveInDatabase) {
          console.log('Coinbase Transaction Created successfully =>', responseSaveInDatabase.data);
          navigate(`/transactions/${responseSaveInDatabase.data.exchange_txn_id}`);
          setCompletingPayment(false);

          form.resetFields(['twoFactorToken']);
          setTwoFactorRequire(false);
          setTwoFAError(null);
        }
      } catch (error) {
        console.log('Transaction Create: Error =>', error);        
        // If the error response indicates an expired token, sign out the user
        if (error.response && error.response.data?.errorCode === 'TOKEN_EXPIRED') {
          auth.signOut();
        }

        setCompletingPayment(false);

        // if metamask error
        if (error.code === 4001) {
          setError("Transaction rejected by the user.");
          console.error("User denied transaction signature.");
          // Return error information
          return;
        }

        const errorData = error.response?.data;
        if(errorData?.errorCode) { // This means the error is our customized error from the server
          setError(errorData.message);
          return;
        }

        setError(error.message); // catch other errors

        console.log('Transaction Create: 2FA Auth required =>', errorData);
        let twoFAErrorString = "";
        // GK 2023-10-22 - the errorData is an array coming from coinbase, we are looking for the "id" key
        errorData?.errors?.forEach((err) => {
            if (err?.id === "two_factor_required") {
              
              setTwoFactorRequire(true); // triggers the 2FA modal
              setError(null); // unset component error message since this already triggers modal
            } else {
              twoFAErrorString += err?.message + "\n";
            }
          });
          
          if(twoFAErrorString) {
            setTwoFAError(twoFAErrorString);
            setError(null); // unset component error message since this will be shown in the modal error 
            return;
          }

        }
  }
  

  const completeSplitPayment = async () => {    
    if(transaction.split_payment_status === 'no') {
      setError('Transaction is not a split payment');
      return;
    }

    if(transaction.split_payment_status !== 'pending') {
      setError('Only pending transaction can be completed');
      return;
    }

    if(transaction.db_source !== 'transactionfee') {
      setError('This record must be a Transaction fee to continue with this request.');
      return;
    }

    if(isNaN(transaction.total_amount_user_entered) || transaction.total_amount_user_entered <= 0) {
      setError('Invalid transaction total_amount_user_entered');
      return;
    }


    if(isNaN(transaction.amount_entered) || transaction.amount_entered <= 0) {
      setError('Invalid transaction amount_entered');
      return;
    }

    if (transaction.total_amount_user_entered <= transaction.amount_entered) {
      setError('transaction total_amount_user_entered must be greater than amount_entered for this operation');
      return;
    }

    if (!transaction.recipient_login_email) {
      setError('Unable to find transaction recipient email');
      return;
    }

    if(signedInWithMetamask) {
      await completeSplitPaymentMetamask();
      return;
    }

    if(signedInWithCoinbase) {
      await completeSplitPaymentCoinbase();
      return;
    }
  }

  const handle2FASubmit = async () => {
    if(completingPayment) return;

    setTwoFAError(null);

    await completeSplitPaymentCoinbase();
  }

  const handle2FACancel = async () => {
    if(completingPayment) return;

    form.resetFields(['twoFactorToken']);
    setTwoFactorRequire(false);

    setTwoFAError(null);
  }
  
  if(inProgress) {
    return <div>Loading...</div>
  }

  // GK 2025-06-26 - show a friendly status explanation based on txn_status
  // const statusMessages = {
  //   waiting_for_clearing: "The transaction has been initiated and is waiting for final clearance. The sender should sign into Coinbase.com and check on this transaction. ",
  //   pending: "The transaction is processing and will be finalized soon. Sender must be signed into Coinbase on Punkypay to allow this status to update.",
  //   failed: "The transaction failed. Please consult your exchange or wallet for more info.",
  //   completed: "Transaction successfully sent.",
  //   Sent: "Transaction successfully sent.",
  // };

  // GK 2026-04-22 - show a friendly status explanation based on txn_status, but do it with more formatting
const statusMessages = {
  waiting_for_clearing: (
    <>
      The transaction has been initiated and is waiting for final clearance.{" "}
      <strong>
        The sender should sign into Coinbase.com and check on this transaction.
      </strong>
    </>
  ),
  pending: (
    <>
      The transaction is processing and will be finalized soon. Sender must be
      signed into Coinbase on Punkypay to allow this status to update.
    </>
  ),
  failed: (
    <>
      The transaction failed. Please consult your exchange or wallet for more
      info.
    </>
  ),
  completed: <>Transaction successfully sent.</>,
  Sent: <>Transaction successfully sent.</>,
};

  const getStatusExplanation = (status) => statusMessages[status] || "";

  const displayExchangeName = input => {
      if (!input) return '';

      if (input.toUpperCase() === "COINBASE") {
          return "Coinbase";
      } else if (input.toUpperCase() === "USERINPUT") {
          return "Punkypay Mailroom";
      } else if (input.toUpperCase() === "GEMINI") {
        return "Gemini";
      } else if (input.toUpperCase() === "METAMASK") {
        return "Metamask";
      } else {
          // Handle other cases if needed
          return "Unknown Exchange";
      }
  }

  const showSplitPaymentMessage = () => {
    if (transaction?.sender_login_email === loginEmail) {
      return <Button 
      onClick={completeSplitPayment} 
      type="primary" 
      htmlType="button"
      disabled={completingPayment || auth?.maintenanceMode?.toLowerCase() === 'on'}
      >
        Complete transaction
      </Button>
    } else if (transaction?.recipient_login_email === loginEmail) {
      return <p>Your ghost fee has been paid from an incomplete transaction. You will recieve the full payment once the sender complete the transaction</p>
    } else {
      return null;
    }
  }


  return (
    <div>

    {
        error && (
          <Row style={{marginBottom: '10px', marginTop: '10px'}}>
            <Col xs={{span: 24}} md={{span: 16, offset: 4}}>
              <Alert
                message={error}
                type="error"
              />
            </Col>
          </Row>
        )
      }

      {
        (!signedInWithCoinbase && !signedInWithMetamask && transaction.split_payment_status === 'pending') && (
          <Row style={{marginBottom: '10px', marginTop: '10px'}}>
            <Col xs={{span: 24}} md={{span: 16, offset: 4}}>
              <Alert
                message={'The sender must remain signed into Coinbase or Metamask to complete this transacttion'}
                type="error"
              />
            </Col>
          </Row>
        )
      }
      <Form
        layout="vertical"
        labelCol={{
            xs: {span: 24},
            md: {span: 12},
            lg: {span: 8}
        }}
        wrapperCol={{
            xs: {span: 24},
            md: {span: 12},
            lg: {span: 8}
        }}
        name="transaction_update"
        form={form}
        onFinish={onFinish}
        initialValues={
          {senderNotes: transaction.sender_notes, recipientNotes: transaction.recipient_notes}
        }
      >
        <p> For more information about this page, <Link class="light-blue" to="/understand-txn-details"> click here</Link></p>
        {
          transaction.split_payment_status === 'pending' && (
            <div>
              { showSplitPaymentMessage() }
            </div>
          )
        }
        <p> <b> Transaction Memo: </b> {transaction.memo_at_txn}</p>

          <Form.Item
              name={"senderNotes"}
              label={<b>Sender's Notes:</b>}
            >
             <TextArea
              className="input-note" 
              style={{ width: "100%", height:"110px" }}
              disabled={transaction?.sender_login_email !== loginEmail} 
              /> 
          </Form.Item>
          {transaction?.sender_login_email === loginEmail && (
            <Form.Item
                    className="form-item form-item__submit"
                    wrapperCol={{
                      span: 24
                    }}
                >
                  <Button type="primary" htmlType="submit" disabled={isUpdating}>
                    Save Updated Notes
                  </Button>
            </Form.Item>
          )}
          <Form.Item
              name={"recipientNotes"}
              label={<b>Recipient's Notes:</b>}
            >
            <TextArea
                className="input-note" 
                style={{ width: "100%", height:"110px" }}
                disabled={transaction?.recipient_login_email !== loginEmail}
              />
            </Form.Item>
            {transaction?.recipient_login_email === loginEmail && (
            <Form.Item
                    className="form-item form-item__submit"
                    wrapperCol={{
                      span: 24
                    }}
                >
                  <Button type="primary" htmlType="submit" disabled={isUpdating}>
                    Save Updated Notes
                  </Button>
            </Form.Item>
            )}
          <ConfigProvider
              theme={{
                algorithm: theme.defaultAlgorithm,
              }}
          >
            <Modal
              title="Two factor Verification"
              closable={!completingPayment && { 'aria-label': 'Custom Close Button' }}
              maskClosable={!completingPayment}
              open={twoFactorRequire}
              onOk={handle2FASubmit}
              onCancel={handle2FACancel}
              okText="Submit"
              confirmLoading={completingPayment}
              cancelButtonProps={{ disabled: completingPayment }}
            >
              <p style={{paddingLeft: '10px'}}>Please enter two-factor authentication code for Coinbase. You will recieve the code on an authenticator app, text message, email, or security prompt.</p>

              {
                twoFAError && (
                  <Row>
                    <Col
                      span={24}
                      style={{marginTop: "5px", marginBottom: "20px"}}
                    >
                      <Alert className="text-center" message={twoFAError} type="error" />
                    </Col>
                  </Row>
                )
              }
              <Form.Item
                name={"twoFactorToken"}
                label={"Two Factor Code"}
                className="form-item"
                wrapperCol={{ span: 24 }}
              >
                <Input placeholder={"Enter Two Factor Code"} maxLength={225} />
              </Form.Item>
            </Modal>
          </ConfigProvider>
      </Form>
      <Row style={{color: '#ffffff'}}>
        <Col span={24}>
          <p> <b> Sender: </b> {transaction.sender_login_email}</p>
          <p> <b> Recipient: </b> {transaction.recipient_login_email}</p>
          <p> <b> Transaction Type: </b> {getTransactionTypeMessage(transaction)}</p>
          <p> <b> Transacion Total: </b> ${Number(transaction.total_amount_user_entered).toFixed(6)}</p>

          { transaction.transactionFee && <h3>Transaction to recipient</h3> }
          
          <p> <b> Amount Sent: </b>{"$" + Number(transaction.txn_amount).toFixed(6)}</p>
          <p> <b> Network Fees: </b>{"$" + Number(transaction.blockchain_fees).toFixed(6)} </p>
          <p> <b> Total: </b> {"$" +( Number(transaction.txn_amount)+Number(transaction.blockchain_fees)).toFixed(6)}</p>
          <p> <b> Currency: </b> {transaction.currency} </p>
          <p> <b> Network: </b> {transaction.network} </p>
          <p className="text-wrap"> <b> Transaction ID (if available): </b> {transaction.network_txn_id ? transaction.network_txn_id : "not available yet"} </p>
          <p className="text-wrap"> <b> Sender's Crypto Exchange: </b> {displayExchangeName(transaction.sender_exchange)}</p>
          <p className="text-wrap"> <b> Sender's Crypto Address: </b> {transaction.sent_from}</p>
          <p className="text-wrap"> <b> Recipient's Crypto Exchange: </b> {displayExchangeName(transaction.recipient_exchange)} </p>
          <p className="text-wrap"> <b> Recipient's Crypto Address: </b> {transaction.sent_to} </p>
          <p> <b> Transaction Initated At: </b> {String(new Date(transaction.created_at))} </p>
          <p> <b> Transaction Info Updated At: </b> {String(new Date(transaction.updated_at))} </p>
          <p> <b> Transaction Status: </b> {transaction.txn_status} </p>
          <p className="status-explanation"><b>Status explanation:</b> {getStatusExplanation(transaction.txn_status)}</p>
          <br></br>
          <p> <i> **Transaction statuses can only be updated if the sender stays signed into Punkypay. If the status has not updated in a while, please email the sender. </i></p>
          
          { transaction.transactionFee && <h3>Transaction Fee made to Punkypay</h3> }
          {
            transaction.transactionFee && (
              <div>
                <p> <b> Amount Sent: </b>{"$" + Number(transaction.transactionFee.txn_amount)}</p>
                <p> <b> Network Fees: </b>{"$" + transaction.transactionFee.blockchain_fees} </p>
                <p> <b> Total: </b> {"$" +( Number(transaction.transactionFee.txn_amount)+Number(transaction.transactionFee.blockchain_fees))}</p>
                <p> <b> Currency: </b> {transaction.transactionFee.currency} </p>
                <p> <b> Network: </b> {transaction.transactionFee.network} </p>
                <p className="text-wrap"> <b> Transaction ID (if available): </b> {transaction.transactionFee.network_txn_id ? transaction.transactionFee.network_txn_id : "not available yet"} </p>
                <p className="text-wrap"> <b> Sender's Crypto Exchange: </b> {displayExchangeName(transaction.transactionFee.sender_exchange)}</p>
                <p className="text-wrap"> <b> Sender's Crypto Address: </b> {transaction.transactionFee.sent_from}</p>
                <p className="text-wrap"> <b> Recipient's Crypto Exchange: </b> {displayExchangeName(transaction.transactionFee.recipient_exchange)} </p>
                <p className="text-wrap"> <b> Recipient's Crypto Address: </b> {transaction.transactionFee.sent_to} </p>
                <p> <b> Transaction Initated At: </b> {String(new Date(transaction.transactionFee.created_at))} </p>
                <p> <b> Transaction Info Updated At: </b> {String(new Date(transaction.transactionFee.updated_at))} </p>
                <p> <b> Transaction Status: </b> {transaction.transactionFee.txn_status} </p>
                <p className="status-explanation"><b>Status explanation:</b> {getStatusExplanation(transaction.transactionFee.txn_status)}</p>
                <br></br>
                <p> <i> **Transaction statuses can only be updated if the sender stays signed into Punkypay. If the status has not updated in a while, please email the sender. </i></p>
                
              </div>
            )
          }
        </Col>
      </Row>
    </div>
  );
};

export default TransactionDetails;
